
import { useState, useEffect, useCallback } from "react";
import { db } from "@/services/firebaseService";
import { LocationVersion } from "@/types/campLocation";
import { toDate } from "@/utils/dateUtils";
import { getCachedLocationVersionHistory } from "@/services/locationVersion/locationVersionCacheService";
import cacheService from "@/services/cacheService";

export function useLocationVersions(locationId: string) {
  const [versions, setVersions] = useState<LocationVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<string | null>(null);
  
  const cacheKey = `locationVersions:${locationId}`;

  const fetchVersions = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check cache first, unless forced refresh
      if (!forceRefresh) {
        const cachedData = cacheService.get<{
          versions: LocationVersion[],
          latest: string | null,
          published: string | null
        }>(cacheKey);
        
        if (cachedData) {
          console.log("[useLocationVersions] Using cached version data");
          setVersions(cachedData.versions);
          setLatestVersion(cachedData.latest);
          setPublishedVersion(cachedData.published);
          setLoading(false);
          return;
        }
      }
      
      console.log("[useLocationVersions] Fetching fresh version history");
      const versionHistory = await getCachedLocationVersionHistory(db, locationId, forceRefresh);
      const sortedVersions = [...versionHistory].sort((a, b) => {
        const dateA = toDate(a.timestamp) || new Date();
        const dateB = toDate(b.timestamp) || new Date();
        return dateB.getTime() - dateA.getTime();
      });
      
      setVersions(sortedVersions);
      
      const published = sortedVersions.find(v => v.status === 'published');
      const latest = sortedVersions[0];
      
      const publishedId = published ? published.versionId : null;
      const latestId = latest ? latest.versionId : null;
      
      setPublishedVersion(publishedId);
      setLatestVersion(latestId);
      
      // Cache the results
      cacheService.set(cacheKey, {
        versions: sortedVersions,
        latest: latestId,
        published: publishedId
      }, 5 * 60 * 1000); // 5 minute cache
      
    } catch (error) {
      setError(`Failed to load version history: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [locationId, cacheKey]);

  // Initial fetch
  useEffect(() => {
    if (locationId) {
      fetchVersions();
    }
  }, [locationId, fetchVersions]);
  
  // Listen for version change events
  useEffect(() => {
    const handleVersionChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ locationId: string }>;
      if (customEvent.detail && customEvent.detail.locationId === locationId) {
        console.log("[useLocationVersions] Version change detected, refreshing");
        fetchVersions(true);
      }
    };
    
    const handleRefreshVersionHistory = () => {
      console.log("[useLocationVersions] Explicit refresh requested");
      fetchVersions(true);
    };
    
    window.addEventListener("locationVersionChanged", handleVersionChange as EventListener);
    window.addEventListener("versionchange", () => fetchVersions(true));
    window.addEventListener("refreshVersionHistory", handleRefreshVersionHistory);
    
    return () => {
      window.removeEventListener("locationVersionChanged", handleVersionChange as EventListener);
      window.removeEventListener("versionchange", () => fetchVersions(true));
      window.removeEventListener("refreshVersionHistory", handleRefreshVersionHistory);
    };
  }, [locationId, fetchVersions]);

  // Also listen for visibility/focus changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVersions(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', () => fetchVersions(true));
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', () => fetchVersions(true));
    };
  }, [fetchVersions]);

  return { 
    versions, 
    loading, 
    error, 
    latestVersion, 
    publishedVersion,
    refreshVersions: fetchVersions // Export refresh function
  };
}
